import Layout from "../../hocs/Layout";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useState, useMemo, useCallback } from "react";
import {
  Button,
  Row,
  Table,
  Badge,
  Container,
} from "react-bootstrap";
import { connect } from "react-redux";
import { get_ofertas_erp, get_ofertas_excel } from "../../services/ofertas";
import { CiWarning } from "react-icons/ci";
import { FaThLarge, FaList, FaTimesCircle } from "react-icons/fa";
import PaginationComponent from "../../components/pagination/Pagination";
import OfertasFilter from "../../components/ofertas/OfertasFilter";
import { sectorTypeBadge, stageOptions, subtipoLabels, getOfferStagePresentation } from "../../utils/ofertasUtils";
import AgentSelector from '../../components/inputs/AgentSelector';
import UserAutocompleteInput from "../../components/inputs/UserAutoCompleteInput";
import OfferListCard from "../../components/cards/OfferListCard";

import "./OfertasList.css";

const tipoOferta = {
  simple: "CONTRATO NUEVO",
  renovacion: "RENOVACIÓN",
};

const formatSubtypeLabel = (subtype) => {
  if (!subtype) return "";

  const mappedLabel = subtipoLabels[subtype];
  if (mappedLabel) return mappedLabel;

  // Fallback for unknown subtype keys coming from ERP (e.g. cambio_titular_potencia)
  return String(subtype)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};


const OfertasList = ({ user, isAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const resolveAgentParamToUserId = (agentParam) => {
    if (agentParam === 'all') return user?.erp_user_id;
    return agentParam || user?.erp_user_id;
  };

  // Parse Query Params to Filters
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialFilters = useMemo(() => {
    const f = {};
    if (queryParams.get("fecha_desde")) f.ofertaFrom = queryParams.get("fecha_desde");
    if (queryParams.get("fecha_hasta")) f.ofertaTo = queryParams.get("fecha_hasta");
    if (queryParams.get("poliza")) f.poliza = queryParams.get("poliza");
    if (queryParams.get("cliente")) f.cliente = queryParams.get("cliente");
    if (queryParams.get("cups")) f.cups = queryParams.get("cups");
    if (queryParams.get("nif")) f.nif = queryParams.get("nif");
    if (queryParams.get("sector")) f.sector = queryParams.get("sector");
    if (queryParams.get("tipo")) f.tipo = queryParams.get("tipo");

    const etapas = queryParams.getAll("etapa");
    if (etapas.length > 0) {
      f.etapa = etapas.map(val => stageOptions.find(o => o.value === parseInt(val, 10))).filter(Boolean);
    }

    if (queryParams.get("pendiente_firma") === 'true') f.pendiente_firma = true;

    return f;
  }, [queryParams]);

  const [loading, setLoading] = useState(false);
  const [ofertas_erp, setOfertasErp] = useState(false);

  // Initialize state from URL params if present, otherwise default
  const [currentPage, setCurrentPage] = useState(() => {
    const page = queryParams.get("page");
    return page ? parseInt(page, 10) : 1;
  });

  const [filters, setFilters] = useState(initialFilters);

  // Agent handling: URL > User Context
  const [selectedUserId, setSelectedUserId] = useState(() => {
    return resolveAgentParamToUserId(queryParams.get("agent"));
  });
  const [agents, setAgents] = useState(() => {
    const agentParam = queryParams.get("agent");
    return agentParam === "all" ? "all" : "";
  });
  const [selectedAgentOption, setSelectedAgentOption] = useState(() => {
    return queryParams.get("agent") || user?.erp_user_id;
  });

  const [selectedUser, setSelectedUser] = useState(null); // For superadmin autocomplete

  // View mode state with localStorage persistence (default: 'list')
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('ofertasViewMode') || 'list';
  });

  const toggleViewMode = () => {
    const newMode = viewMode === 'list' ? 'cards' : 'list';
    setViewMode(newMode);
    localStorage.setItem('ofertasViewMode', newMode);
  };

  // Helper to update URL with current state + new changes
  const updateUrl = (newParams) => {
    const params = new URLSearchParams(location.search);

    // Merge new params
    Object.keys(newParams).forEach(key => {
      const value = newParams[key];
      if (value === null || value === undefined || value === "") {
        params.delete(key);
      } else if (Array.isArray(value)) {
        params.delete(key);
        value.forEach(v => params.append(key, v));
      } else {
        params.set(key, value);
      }
    });

    navigate({ search: params.toString() }, { replace: true });
  };

  // Sync State from URL changes
  useEffect(() => {
    // Filters
    setFilters(initialFilters);

    // Page
    const pageParam = queryParams.get("page");
    setCurrentPage(pageParam ? parseInt(pageParam, 10) : 1);

    // Agent
    const agentParam = queryParams.get("agent");
    if (agentParam) {
      if (agentParam === 'all') {
        setSelectedUserId(resolveAgentParamToUserId(agentParam));
        setAgents('all');
        setSelectedAgentOption('all');
        setSelectedUser('all'); // Pass string 'all' to UserAutocompleteInput
      } else {
        setSelectedUserId(resolveAgentParamToUserId(agentParam));
        setAgents('');
        setSelectedAgentOption(agentParam);
        setSelectedUser(agentParam); // Pass user ID string to UserAutocompleteInput
      }
    } else {
      // Reset to default user when no agent param
      setSelectedUserId(resolveAgentParamToUserId(null));
      setSelectedUser(null);
    }

  }, [initialFilters, queryParams, user]);


  const getOfertas = useCallback(async (page) => {
    const targetUserId = resolveAgentParamToUserId(selectedUserId);
    if (!targetUserId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setOfertasErp(await get_ofertas_erp(targetUserId, page, agents, filters));
    setLoading(false);
  }, [selectedUserId, agents, filters, user]);

  useEffect(() => {
    if (isAuthenticated) {
      getOfertas(currentPage);
    }
    window.scrollTo(0, 0);
  }, [isAuthenticated, currentPage, getOfertas]);

  const handlePageChange = (page) => {
    updateUrl({ page });
  };

  const handleFilter = (newFilters) => {
    // Map internal filter object to URL params
    const params = {
      page: 1, // Reset to page 1 on filter change
      poliza: newFilters.poliza,
      cliente: newFilters.cliente,
      cups: newFilters.cups,
      nif: newFilters.nif,
      sector: newFilters.sector,
      tipo: newFilters.tipo,
      fecha_desde: newFilters.ofertaFrom,
      fecha_hasta: newFilters.ofertaTo,
      pendiente_firma: newFilters.pendiente_firma ? 'true' : null, // Only set if true
    };

    // Etapa is special (array of objects)
    if (newFilters.etapa && newFilters.etapa.length > 0) {
      params.etapa = newFilters.etapa.map(e => e.value);
    } else {
      params.etapa = null; // Clear it
    }

    updateUrl(params);
  };

  const handleExportExcel = async () => {
    await get_ofertas_excel(selectedUserId, agents, filters);
  }

  const handleAgentSelect = (agentUserId) => {
    updateUrl({
      agent: agentUserId,
      page: 1
    });
  };



  return (
    <Layout lg={11} md={12}>
      <div className="py-5 bg-light min-vh-100">
        <Container style={{ width: "1480px", maxWidth: "100%", margin: "0 auto" }}>
          {(user.is_superadmin || user.is_internal_agent || user.is_call_agent) && (
            <UserAutocompleteInput
              value={selectedUser}
              placeholder="Nombre del usuario..."
              
              delegacionId={user.delegacion}
              isInternalAgent={user.is_internal_agent}
              isCallAgent={user.is_call_agent}
              isSuperAdmin={user.is_superadmin}

              onChange={(selectedOption) => {
                if (selectedOption === 'all') {
                  handleAgentSelect(selectedOption);
                }
                else {
                  setSelectedUser(selectedOption);
                  handleAgentSelect(selectedOption ? selectedOption.value : selectedAgentOption);
                }
              }}
            />
          )}

          {!user.is_superadmin && user.agents && user.agents.length > 0 && (
            <AgentSelector
              agents={user.agents}
              selectedAgent={selectedAgentOption}
              onSelectAgent={handleAgentSelect}
            />
          )}

          <OfertasFilter
            onFilter={handleFilter}
            onExport={handleExportExcel}
            initialFilters={filters}
            user={user}
            isSuperAdmin={user?.is_superadmin}
          />

          {/* Floating View Toggle Button */}
          <Button
            onClick={toggleViewMode}
            className="position-fixed rounded-circle shadow-lg border-0"
            variant="primary"
            style={{
              bottom: '2rem',
              right: '2rem',
              width: '60px',
              height: '60px',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title={viewMode === 'list' ? 'Vista de tarjetas' : 'Vista de lista'}
          >
            {viewMode === 'list' ? <FaThLarge size={24} /> : <FaList size={24} />}
          </Button>

          {loading ? (
            <></>
          ) : (ofertas_erp && ofertas_erp.ofertas && ofertas_erp.ofertas.length > 0) ? (
            <>
              {viewMode === 'cards' ? (
                <Row className="mt-2">
                  {ofertas_erp.ofertas.map((oferta) => (
                    <OfferListCard key={oferta.id} oferta={oferta} />
                  ))}
                </Row>
              ) : (
                <div className="mt-4">
                  <Table hover responsive className="bg-white rounded-4 overflow-hidden shadow-sm">
                    <thead className="bg-light">
                      <tr>
                        <th className="fw-bold text-secondary">Oferta</th>
                        <th className="fw-bold text-secondary">Cliente</th>
                        <th className="fw-bold text-secondary">Tipo</th>
                        <th className="fw-bold text-secondary">Subtipo</th>
                        <th className="fw-bold text-secondary">Sector</th>
                        <th className="fw-bold text-secondary">CUPS</th>
                        <th className="fw-bold text-secondary">Etapa</th>
                        <th className="fw-bold text-secondary">Fecha</th>
                        <th className="fw-bold text-secondary">Agente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ofertas_erp.ofertas.map((oferta) => {
                        const clientName = oferta.cliente_nombre || oferta.cliente_apellidos
                          ? `${oferta.cliente_nombre || ''} ${oferta.cliente_apellidos || ''}`.trim()
                          : 'Cliente no asignado';
                        const stagePresentation = getOfferStagePresentation(oferta);

                        return (
                          <tr
                            key={oferta.id}
                            onClick={() => navigate(`/contratos/ofertas/${oferta.id}${location.search}`)}
                            style={{ cursor: 'pointer' }}
                            className="align-middle"
                          >
                            <td className="fw-bold text-primary">{oferta.name || `#${oferta.id}`}</td>
                            <td>
                              <div className="fw-bold">{clientName}</div>
                              <small className="text-muted">{oferta.cliente_vat || ''}</small>
                            </td>
                            <td>
                              <Badge pill bg="light" className="text-dark border small fw-normal">
                                {tipoOferta[oferta.type] || oferta.type}
                              </Badge>
                            </td>
                            <td>
                              {oferta.sub_type_offer && (
                                <Badge pill bg="secondary" className="small fw-normal">
                                  {formatSubtypeLabel(oferta.sub_type_offer)}
                                </Badge>
                              )}
                            </td>
                            <td>{sectorTypeBadge(oferta.sector_type)}</td>
                            <td className="text-muted">
                              {oferta.cups_name || 'N/A'}
                            </td>
                            <td>
                              <Badge
                                pill
                                bg={stagePresentation.hasSignaturitError ? 'danger' : 'primary'}
                                className="px-3 py-2 d-inline-flex align-items-center gap-2"
                              >
                                {stagePresentation.hasSignaturitError && <FaTimesCircle aria-hidden="true" />}
                                {stagePresentation.label}
                              </Badge>
                            </td>
                            <td className="text-muted">
                              {oferta.create_date || 'N/A'}
                            </td>
                            <td className="text-muted">
                              {/* Using prescriptor_nombre if available, otherwise N/A */}
                              {oferta.prescriptor_nombre || oferta.prescriptor || 'N/A'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
              <PaginationComponent
                totalPages={ofertas_erp.total_pages}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                totalRecords={ofertas_erp.total_records}>
              </PaginationComponent>
            </>
          ) : (
            <div className="d-flex flex-column justify-content-center align-items-center py-5 my-5 text-center opacity-75">
              <div className="mb-3 p-4 rounded-circle bg-light text-secondary d-inline-flex">
                <CiWarning size={64} />
              </div>
              <h2 className="text-secondary fw-bold mb-4">No se han encontrado ofertas</h2>
              <Button
                className="rounded-pill px-5 py-2 fw-bold shadow"
                variant="primary"
                onClick={() => getOfertas(currentPage)}
              >
                Reintentar
              </Button>
            </div>
          )}
        </Container>
      </div>
    </Layout>
  );
};

const mapStateToProps = (state) => ({
  user: state.Auth.user,
  isAuthenticated: state.Auth.isAuthenticated,
});

export default connect(mapStateToProps)(OfertasList);
